import { useEffect } from 'react';
import { useEdges, useNodes } from '@xyflow/react';
import { Select } from 'antd';
import { CustomSelectIcon } from '@/assets/icons/icons';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import type { ReactFlowEdge, ReactFlowNode } from '@/pages/Logiblocks/types';
import styles from '../NodeCustomization.module.css';
import { FieldErrorLabel, FieldLabel } from '../shared';
import { BackToDelayFields } from './BackToDelayNodeInfo';
import { delayLabel, getDelaysAbove } from './delaysAbove';

/**
 * Customization UI for BACK_TO_DELAY nodes: which Delay until event the run goes back to. The
 * nearest one above is chosen to begin with.
 */
export const BackToDelayCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const nodes = useNodes<ReactFlowNode>();
  const edges = useEdges<ReactFlowEdge>();
  const delays = getDelaysAbove(nodeId, nodes, edges);
  const returnTo = nodeData.back_to_delay?.return_to_node_id ?? '';
  const nearest = delays[0];

  useEffect(() => {
    if (!returnTo && nearest && !isReadOnly) {
      onChange({ back_to_delay: { return_to_node_id: nearest.id } });
    }
    // Only a step with no choice yet gets the default; picking later is the author's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nearest?.id]);

  const error = validationErrors?.find(
    (e) => e.field === BackToDelayFields.RETURN_TO
  );

  return (
    <div className={styles.customizationSection}>
      <FieldLabel required className={styles.sectionLabel}>
        {BackToDelayFields.RETURN_TO}
      </FieldLabel>
      <Select
        aria-label={BackToDelayFields.RETURN_TO}
        // A Delay that is no longer above shows as a gap, with the error beneath, not as a raw id.
        value={delays.some((d) => d.id === returnTo) ? returnTo : undefined}
        placeholder='Select'
        options={delays.map((delay) => ({
          value: delay.id,
          label: delayLabel(delay, nodes),
        }))}
        onChange={(id: string) =>
          onChange({ back_to_delay: { return_to_node_id: id } })
        }
        disabled={isReadOnly}
        suffixIcon={<CustomSelectIcon />}
        className='agentSelect'
        style={{ width: '100%' }}
      />
      <FieldErrorLabel error={error} />
    </div>
  );
};
